/**
 * Pure path helpers for the logged-out public Publish surface.
 *
 * SECURITY: `/p` and `/p/...` allow anonymous access without a Buffer session.
 * Cloudflare exempts this prefix from its login redirect on publish.buffer.com.
 * This helper selects frontend behavior; it does not authorize API access.
 * Never widen the check to `/p*`, which also matches private routes like `/posts`.
 * See src/components/PublicPublishShell/README.md before adding public behavior.
 *
 * Public preview routes live under the `/p` prefix. The prefix check is the
 * boot-time gate for the whole public flow: `PublishRoot` uses it to choose
 * between the legacy authenticated app and the public shell, and the Apollo
 * error link uses it to decide whether bootstrap auth rejections may resolve
 * to a logged-out fallback instead of redirecting to login.
 *
 * Kept free of app imports so the Apollo client module can depend on it at
 * module-evaluation time without dragging route tables into its import graph.
 */
export const PUBLIC_PUBLISH_ROUTE_PREFIX = '/p'

/**
 * Treat trailing slash variants as the same canonical path while preserving
 * the root path.
 */
export function normalizePathname(pathname: string): string {
  return pathname.replace(/\/+$/, '') || '/'
}

/**
 * True for `/p` and any `/p/...` path. Prefix-only paths like `/preferences`
 * or `/posts` do not match.
 */
export function isPublicPublishPath(pathname: string): boolean {
  const normalizedPathname = normalizePathname(pathname)

  return (
    normalizedPathname === PUBLIC_PUBLISH_ROUTE_PREFIX ||
    normalizedPathname.startsWith(`${PUBLIC_PUBLISH_ROUTE_PREFIX}/`)
  )
}

/**
 * Maps a `/p/...` public path to its authenticated app equivalent, or `null`
 * when the path is not under the public prefix. The exact `/p` path maps to
 * the app root.
 */
export function getAuthenticatedPathnameFromPublicPath(
  pathname: string,
): string | null {
  const normalizedPathname = normalizePathname(pathname)

  if (normalizedPathname === PUBLIC_PUBLISH_ROUTE_PREFIX) {
    return '/'
  }

  if (!normalizedPathname.startsWith(`${PUBLIC_PUBLISH_ROUTE_PREFIX}/`)) {
    return null
  }

  const remainder = normalizedPathname.slice(PUBLIC_PUBLISH_ROUTE_PREFIX.length)

  // Collapse repeated leading separators: a remainder like `//evil.com` (from
  // `/p//evil.com`) would resolve as a protocol-relative URL when the login
  // handoff or the authenticated redirect turns it back into a destination.
  return `/${remainder.replace(/^[/\\]+/, '')}`
}
